// src/components/login.jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  ArrowRight,
  BrainCircuit,
  CalendarDays,
  CheckCircle2,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  LogIn,
  Mail,
  ShieldCheck,
  Sparkles,
  UserPlus,
  WalletCards,
} from "lucide-react";

import Logo from "../assets/logoClaro.webp";

import {
  AUTH_KEYS,
  limparSessaoPsyAssist,
  salvarSessaoPsyAssist,
} from "../services/authStorage";

import { useToast } from "../context/ToastContext";

import "../sass/pages/login.scss";

const API_BASE = String(
  process.env.REACT_APP_API_URL ||
    "https://api.psyassist.com.br"
).replace(/\/+$/, "");

const LOGIN_KEYS = {
  LAST_EMAIL: "psy_last_email",
  REMEMBER_ME: "psy_remember_me",

  /*
   * Chave antiga removida por segurança.
   * Senhas não devem permanecer no localStorage,
   * mesmo quando codificadas em Base64.
   */
  LEGACY_LAST_PASS: "psy_last_pass_obf",
};

const AUTH_STORAGE_KEYS = Array.from(
  new Set([
    ...Object.values(AUTH_KEYS),

    "plano",
    "planoOriginal",
    "planoNormalizado",
    "valorPlano",

    "premium",
    "premiumLiberado",

    "statusAssinatura",
    "assinaturaAtiva",
    "assinaturaAtivaBanco",
    "assinaturaValida",

    "avisoAssinatura",
    "avisoDowngrade",
    "recursos",

    "stripePriceId",
    "currentPeriodEnd",
    "vencimentoAssinatura",

    "isTrial",
    "trialEndsAt",

    "planoAgendado",
    "valorPlanoAgendado",
    "stripePriceIdAgendado",

    "downgradeAgendado",
    "downgradeJaEfetivo",
    "downgradeSolicitadoEm",
    "downgradeEfetivoEm",
    "downgradeScheduleId",
  ])
);

const isEmailValid = (value) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(
    String(value || "").trim()
  );
};

const removeStorageKeys = (storage) => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    try {
      storage.removeItem(key);
    } catch {
      // O login não deve falhar por indisponibilidade pontual do storage.
    }
  });
};

const applyPersistencePreference = (rememberMe) => {
  /*
   * salvarSessaoPsyAssist mantém a gravação centralizada.
   *
   * Depois preservamos somente o storage correspondente:
   * - marcado: localStorage;
   * - desmarcado: sessionStorage.
   */
  if (rememberMe) {
    removeStorageKeys(sessionStorage);
    return;
  }

  removeStorageKeys(localStorage);
};

const normalizeLoginResponse = (
  data = {},
  informedEmail = ""
) => {
  return {
    ...data,

    accessToken:
      data.accessToken ||
      data.token ||
      "",

    id:
      data.id ||
      data.userId ||
      data._id ||
      "",

    nome:
      data.nome ||
      data.nomeUsuario ||
      "",

    email:
      data.email ||
      informedEmail,

    plano:
      data.plano ||
      "essencial",

    planoOriginal:
      data.planoOriginal ||
      data.plano ||
      "essencial",

    planoNormalizado:
      data.planoNormalizado ||
      data.plano ||
      "essencial",

    valorPlano:
      data.valorPlano ?? 0,

    premium:
      data.premium === true,

    premiumLiberado:
      data.premiumLiberado === true,

    statusAssinatura:
      data.statusAssinatura ||
      "",

    assinaturaAtiva:
      data.assinaturaAtiva === true,

    assinaturaAtivaBanco:
      data.assinaturaAtivaBanco === true,

    assinaturaValida:
      data.assinaturaValida === true,

    recursos:
      data.recursos &&
      typeof data.recursos === "object"
        ? data.recursos
        : {},

    planoAgendado:
      data.planoAgendado ||
      null,

    valorPlanoAgendado:
      data.valorPlanoAgendado ??
      null,

    stripePriceIdAgendado:
      data.stripePriceIdAgendado ||
      null,

    downgradeAgendado:
      data.downgradeAgendado === true,

    downgradeJaEfetivo:
      data.downgradeJaEfetivo === true,

    downgradeEfetivoEm:
      data.downgradeEfetivoEm ||
      null,

    downgradeSolicitadoEm:
      data.downgradeSolicitadoEm ||
      null,

    downgradeScheduleId:
      data.downgradeScheduleId ||
      null,

    currentPeriodEnd:
      data.currentPeriodEnd ||
      null,

    vencimentoAssinatura:
      data.vencimentoAssinatura ||
      null,

    isTrial:
      data.isTrial === true,

    trialEndsAt:
      data.trialEndsAt ||
      null,
  };
};

const readApiPayload = async (response) => {
  const contentType =
    response.headers.get("content-type") ||
    "";

  try {
    if (
      contentType.includes(
        "application/json"
      )
    ) {
      const data =
        await response.json();

      return data &&
        typeof data === "object"
        ? data
        : {};
    }

    const text =
      await response.text();

    return text
      ? {
          message: text,
        }
      : {};
  } catch {
    return {};
  }
};

const getLoginErrorMessage = (
  data,
  status
) => {
  if (
    data &&
    typeof data === "object" &&
    typeof data.message === "string" &&
    data.message.trim()
  ) {
    return data.message.trim();
  }

  if (status === 400) {
    return "E-mail ou senha inválidos.";
  }

  if (status === 401) {
  return "E-mail ou senha inválidos.";
}

  if (status === 403) {
    return "Seu acesso está temporariamente indisponível.";
  }

  if (status === 423) {
    return "Esta conta está bloqueada temporariamente.";
  }

  if (status === 429) {
    return "Muitas tentativas de acesso. Aguarde alguns minutos e tente novamente.";
  }

  if (status >= 500) {
    return "O serviço de autenticação está temporariamente indisponível.";
  }

  return `Não foi possível realizar o login. Código HTTP ${status}.`;
};

function LoginField({
  id,
  label,
  error,
  children,
}) {
  return (
    <div
      className={`psy-login-field ${
        error
          ? "psy-login-field--error"
          : ""
      }`}
    >
      <label
        htmlFor={id}
        className="psy-login-field__label"
      >
        {label}
      </label>

      {children}

      {error ? (
        <p
          id={`${id}-error`}
          className="psy-login-field__error"
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const noticeHandledRef =
    useRef(false);

  const emailInputRef =
    useRef(null);

  const [email, setEmail] =
    useState("");

  const [senha, setSenha] =
    useState("");

  const [
    rememberMe,
    setRememberMe,
  ] = useState(true);

  const [
    showSenha,
    setShowSenha,
  ] = useState(false);

  const [
    capsLockActive,
    setCapsLockActive,
  ] = useState(false);

  const [errors, setErrors] =
    useState({
      email: "",
      senha: "",
    });

  const [
    isLoading,
    setIsLoading,
  ] = useState(false);

  const normalizedEmail =
    useMemo(() => {
      return String(email || "")
        .trim()
        .toLowerCase();
    }, [email]);

  useEffect(() => {
    try {
      const savedEmail =
        localStorage.getItem(
          LOGIN_KEYS.LAST_EMAIL
        ) || "";

      const savedRemember =
        localStorage.getItem(
          LOGIN_KEYS.REMEMBER_ME
        );

      const remember =
        savedRemember === null
          ? true
          : savedRemember === "true";

      setRememberMe(remember);
      setEmail(savedEmail);

      /*
       * Remove credencial legada salva por versões anteriores.
       */
      localStorage.removeItem(
        LOGIN_KEYS.LEGACY_LAST_PASS
      );

      sessionStorage.removeItem(
        LOGIN_KEYS.LEGACY_LAST_PASS
      );
    } catch {
      // Mantém os valores padrão caso o navegador bloqueie o storage.
    }
  }, []);

  useEffect(() => {
    const notice =
      location.state?.notice;

    if (
      noticeHandledRef.current ||
      !notice?.message
    ) {
      return;
    }

    noticeHandledRef.current = true;

    const type = [
      "success",
      "error",
      "warning",
      "info",
    ].includes(notice.type)
      ? notice.type
      : "info";

    toast[type]({
      title:
        notice.title ||
        "PsyAssist Pro",
      message:
        notice.message,
    });

    navigate(
      location.pathname,
      {
        replace: true,
        state: null,
      }
    );
  }, [
    location.pathname,
    location.state,
    navigate,
    toast,
  ]);

  const saveLastLoginPreference =
    () => {
      try {
        localStorage.setItem(
          LOGIN_KEYS.LAST_EMAIL,
          normalizedEmail
        );

        localStorage.setItem(
          LOGIN_KEYS.REMEMBER_ME,
          String(rememberMe)
        );

        /*
         * Segurança:
         * somente o e-mail e a preferência de sessão são armazenados.
         */
        localStorage.removeItem(
          LOGIN_KEYS.LEGACY_LAST_PASS
        );
      } catch {
        // O login continua mesmo sem persistência local.
      }
    };

  const clearFieldError =
    (field) => {
      setErrors((current) => ({
        ...current,
        [field]: "",
      }));
    };

  const validateForm = () => {
    const nextErrors = {
      email: "",
      senha: "",
    };

    if (!normalizedEmail) {
      nextErrors.email =
        "Informe seu e-mail.";
    } else if (
      !isEmailValid(
        normalizedEmail
      )
    ) {
      nextErrors.email =
        "Informe um endereço de e-mail válido.";
    }

    if (!senha) {
      nextErrors.senha =
        "Informe sua senha.";
    } else if (
      senha.length < 4
    ) {
      /*
       * Mantém compatibilidade com contas antigas.
       * A regra de criação de novas senhas pode ser mais restritiva.
       */
      nextErrors.senha =
        "A senha precisa ter pelo menos 4 caracteres.";
    }

    setErrors(nextErrors);

    const isValid =
      !nextErrors.email &&
      !nextErrors.senha;

    if (!isValid) {
      toast.warning({
        title:
          "Revise os dados",
        message:
          "Existem campos que precisam da sua atenção.",
      });

      window.setTimeout(() => {
        const firstInvalid =
          document.querySelector(
            ".psy-login-field--error input"
          );

        firstInvalid?.focus();
      }, 80);
    }

    return isValid;
  };

  const handleLogin = async (
    event
  ) => {
    event.preventDefault();

    if (isLoading) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    /*
     * A senha é enviada exatamente como foi digitada.
     * Não use trim em credenciais, pois espaços podem fazer parte dela.
     */
    const informedPassword =
      String(senha || "");

    setIsLoading(true);

    try {
      /*
       * Evita misturar token, plano e recursos de uma sessão anterior.
       */
      limparSessaoPsyAssist();

      const response =
  await fetch(
    `${API_BASE}/psy/login`,
    {
      method: "POST",
      headers: {
        "Content-Type":
          "application/json",
        Accept:
          "application/json",
      },
      body: JSON.stringify({
        email:
          normalizedEmail,
        senha:
          informedPassword,
      }),
    }
  );

      const data =
        await readApiPayload(
          response
        );

      if (!response.ok) {
        const loginError =
          new Error(
            getLoginErrorMessage(
              data,
              response.status
            )
          );

        loginError.status =
          response.status;

        throw loginError;
      }

      if (
        data?.ativo === false
      ) {
        const inactiveError =
          new Error(
            "Esta conta está desativada. Entre em contato com o suporte."
          );

        inactiveError.status =
          403;

        throw inactiveError;
      }

      const session =
        normalizeLoginResponse(
          data,
          normalizedEmail
        );

      if (
        !session.accessToken
      ) {
        throw new Error(
          "O servidor não retornou o token de acesso."
        );
      }

      if (!session.id) {
        throw new Error(
          "O servidor não retornou a identificação do usuário."
        );
      }

      /*
       * Único ponto responsável por persistir:
       * token, usuário, plano, assinatura, recursos,
       * trial, Premium e downgrade.
       */
      salvarSessaoPsyAssist(
        session
      );

      applyPersistencePreference(
        rememberMe
      );

      saveLastLoginPreference();

      toast.success({
        title:
          "Acesso liberado",
        message:
          `Bem-vindo${session.nome ? `, ${session.nome}` : ""}. Seu workspace está pronto.`,
      });

      navigate(
        "/dashboard",
        {
          replace: true,
        }
      );
    } catch (error) {
      console.error(
        "Erro ao realizar login:",
        error
      );

      const persistent =
        error?.status === 403 ||
        error?.status === 423 ||
        error?.status === 429;

      toast.error({
        title:
          "Não foi possível entrar",
        message:
          error?.message ||
          "Erro de comunicação. Verifique sua conexão e tente novamente.",
        duration:
          persistent
            ? 0
            : 6000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordKeyState =
    (event) => {
      setCapsLockActive(
        Boolean(
          event.getModifierState?.(
            "CapsLock"
          )
        )
      );
    };

  return (
    <main className="psy-login-page">
      <div
        className="psy-login-background"
        aria-hidden="true"
      >
        <span className="psy-login-background__orb psy-login-background__orb--one" />
        <span className="psy-login-background__orb psy-login-background__orb--two" />
        <span className="psy-login-background__grid" />
      </div>

      <div className="psy-login-layout">
        <aside className="psy-login-aside">
          <Link
            to="/"
            className="psy-login-brand"
            aria-label="Voltar para a página inicial"
          >
            <img
              src={Logo}
              alt="PsyAssist Pro"
            />

            <div>
              <strong>
                PsyAssist Pro
              </strong>

              <span>
                Inteligência para sua rotina profissional
              </span>
            </div>
          </Link>

          <div className="psy-login-aside__content">
            <span className="psy-login-kicker">
              <Sparkles size={15} />
              Seu workspace
            </span>

            <h1>
              Sua prática organizada,
              conectada e mais inteligente.
            </h1>

            <p>
              Acesse o ambiente que centraliza
              agenda, pacientes, sessões,
              financeiro e a inteligência da
              PSYA.
            </p>

            <div className="psy-login-capabilities">
              <div className="psy-login-capability">
                <span>
                  <CalendarDays size={19} />
                </span>

                <div>
                  <strong>
                    Rotina sob controle
                  </strong>

                  <small>
                    Agenda, confirmações e
                    acompanhamento em um único
                    fluxo.
                  </small>
                </div>
              </div>

              <div className="psy-login-capability">
                <span>
                  <WalletCards size={19} />
                </span>

                <div>
                  <strong>
                    Visão operacional
                  </strong>

                  <small>
                    Sessões, receitas e
                    pendências organizadas com
                    contexto.
                  </small>
                </div>
              </div>

              <div className="psy-login-capability">
                <span>
                  <BrainCircuit size={19} />
                </span>

                <div>
                  <strong>
                    Inteligência da PSYA
                  </strong>

                  <small>
                    Insights e recomendações de
                    apoio sem substituir sua
                    decisão profissional.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="psy-login-security-card">
            <div>
              <ShieldCheck size={19} />
            </div>

            <p>
              <strong>
                Ambiente protegido
              </strong>

              Sua senha não é armazenada nesta
              página. A persistência mantém
              somente a sessão autorizada.
            </p>
          </div>
        </aside>

        <section className="psy-login-panel">
          <div className="psy-login-panel__top">
            <span>
              Ainda não utiliza o PsyAssist?
            </span>

            <button
              type="button"
              onClick={() =>
                navigate("/register")
              }
              disabled={isLoading}
            >
              Criar conta
              <ArrowRight size={15} />
            </button>
          </div>

          <div className="psy-login-content">
            <header className="psy-login-header">
              <div
                className="psy-login-header__icon"
                aria-hidden="true"
              >
                <LogIn size={22} />
              </div>

              <div>
                <span>
                  Acesso seguro
                </span>

                <h2>
                  Bem-vindo de volta
                </h2>

                <p>
                  Entre com as credenciais
                  cadastradas para acessar seu
                  workspace.
                </p>
              </div>
            </header>

            <form
              className="psy-login-form"
              onSubmit={handleLogin}
              noValidate
              aria-busy={isLoading}
            >
              <LoginField
                id="psy-login-email"
                label="E-mail"
                error={errors.email}
              >
                <div className="psy-login-control">
                  <Mail
                    size={18}
                    aria-hidden="true"
                  />

                  <input
                    ref={emailInputRef}
                    id="psy-login-email"
                    name="email"
                    type="email"
                    placeholder="nome@clinica.com.br"
                    value={email}
                    onChange={(event) => {
                      setEmail(
                        event.target.value
                      );

                      clearFieldError(
                        "email"
                      );
                    }}
                    autoComplete="email"
                    inputMode="email"
                    disabled={isLoading}
                    aria-invalid={Boolean(
                      errors.email
                    )}
                    aria-describedby={
                      errors.email
                        ? "psy-login-email-error"
                        : undefined
                    }
                  />
                </div>
              </LoginField>

              <LoginField
                id="psy-login-password"
                label="Senha"
                error={errors.senha}
              >
                <div className="psy-login-control">
                  <LockKeyhole
                    size={18}
                    aria-hidden="true"
                  />

                  <input
                    id="psy-login-password"
                    name="senha"
                    type={
                      showSenha
                        ? "text"
                        : "password"
                    }
                    placeholder="Digite sua senha"
                    value={senha}
                    onChange={(event) => {
                      setSenha(
                        event.target.value
                      );

                      clearFieldError(
                        "senha"
                      );
                    }}
                    onKeyDown={
                      handlePasswordKeyState
                    }
                    onKeyUp={
                      handlePasswordKeyState
                    }
                    onBlur={() =>
                      setCapsLockActive(
                        false
                      )
                    }
                    autoComplete="current-password"
                    disabled={isLoading}
                    aria-invalid={Boolean(
                      errors.senha
                    )}
                    aria-describedby={
                      errors.senha
                        ? "psy-login-password-error"
                        : capsLockActive
                          ? "psy-login-caps-lock"
                          : undefined
                    }
                  />

                  <button
                    type="button"
                    className="psy-login-password-toggle"
                    onClick={() =>
                      setShowSenha(
                        (current) =>
                          !current
                      )
                    }
                    disabled={isLoading}
                    aria-label={
                      showSenha
                        ? "Ocultar senha"
                        : "Exibir senha"
                    }
                  >
                    {showSenha ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>

                {capsLockActive ? (
                  <p
                    id="psy-login-caps-lock"
                    className="psy-login-caps-lock"
                  >
                    Caps Lock está ativado.
                  </p>
                ) : null}
              </LoginField>

              <div className="psy-login-options">
                <label className="psy-login-remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) =>
                      setRememberMe(
                        event.target.checked
                      )
                    }
                    disabled={isLoading}
                  />

                  <span
                    className="psy-login-remember__control"
                    aria-hidden="true"
                  />

                  <span className="psy-login-remember__copy">
                    <strong>
                      Manter conectado
                    </strong>

                    <small>
                      Somente neste dispositivo
                    </small>
                  </span>
                </label>

                <button
                  type="button"
                  className="psy-login-forgot"
                  onClick={() =>
                    navigate(
                      "/forgot-password"
                    )
                  }
                  disabled={isLoading}
                >
                  Esqueci minha senha
                </button>
              </div>

              <button
                type="submit"
                className="psy-login-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <LoaderCircle
                      size={18}
                      className="psy-login-spinner"
                    />

                    Autenticando...
                  </>
                ) : (
                  <>
                    Entrar no PsyAssist
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            <section className="psy-login-signup">
              <div className="psy-login-signup__icon">
                <UserPlus size={19} />
              </div>

              <div>
                <strong>
                  Começando agora?
                </strong>

                <span>
                  Crie seu workspace e configure
                  sua conta em poucos passos.
                </span>
              </div>

              <button
                type="button"
                onClick={() =>
                  navigate("/register")
                }
                disabled={isLoading}
              >
                Criar conta
              </button>
            </section>
          </div>

          <footer className="psy-login-footer">
            <CheckCircle2 size={14} />

            <p>
              Ao continuar, você concorda com os{" "}
              <Link to="/termos-de-uso">
                Termos de uso
              </Link>{" "}
              e a{" "}
              <Link to="/privacidade">
                Política de privacidade
              </Link>
              .
            </p>
          </footer>
        </section>
      </div>
    </main>
  );
};

export default Login;
